import React, { useMemo } from 'react'
import Map, { Source, Layer } from 'react-map-gl/mapbox'
import { Box, Typography, useTheme } from '@mui/material'

const MAPBOX_TOKEN = process.env.REACT_APP_MAPBOX_TOKEN

const NODE_COLORS = {
  supplier: '#10b981',
  factory: '#f59e0b',
  distributor: '#3b82f6',
  retailer: '#8b5cf6',
}

function MapMinimap({ viewState, nodes, themeMode }) {
  const theme = useTheme()
  const isDark = theme.palette.mode === 'dark'

  const minimapViewState = useMemo(() => ({
    longitude: viewState.longitude,
    latitude: viewState.latitude,
    zoom: Math.max(0, (viewState.zoom || 4) - 3.5),
    bearing: 0,
    pitch: 0,
  }), [viewState.longitude, viewState.latitude, viewState.zoom])

  const nodeGeojson = useMemo(() => ({
    type: 'FeatureCollection',
    features: nodes
      .filter(n => n.data.lat != null && n.data.lng != null)
      .map(n => ({
        type: 'Feature',
        properties: {
          color: NODE_COLORS[n.data.nodeType] || NODE_COLORS.supplier,
        },
        geometry: { type: 'Point', coordinates: [n.data.lng, n.data.lat] },
      })),
  }), [nodes])

  const mapStyle = isDark
    ? 'mapbox://styles/mapbox/dark-v11'
    : 'mapbox://styles/mapbox/light-v11'

  return (
    <Box
      sx={{
        position: 'absolute',
        bottom: 36,
        right: 16,
        width: 164,
        height: 112,
        borderRadius: '10px',
        overflow: 'hidden',
        border: `1.5px solid ${isDark ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.12)'}`,
        boxShadow: isDark
          ? '0 4px 24px rgba(0,0,0,0.55)'
          : '0 4px 24px rgba(0,0,0,0.18)',
        zIndex: 5,
      }}
    >
      <Map
        id="minimap"
        {...minimapViewState}
        mapboxAccessToken={MAPBOX_TOKEN}
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyle}
        interactive={false}
        attributionControl={false}
        reuseMaps
      >
        <Source id="minimap-nodes" type="geojson" data={nodeGeojson}>
          <Layer
            id="minimap-circles"
            type="circle"
            paint={{
              'circle-radius': 5,
              'circle-color': ['get', 'color'],
              'circle-stroke-width': 1.5,
              'circle-stroke-color': '#ffffff',
            }}
          />
        </Source>
      </Map>

      {/* Label bar at bottom */}
      <Box
        sx={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          px: 1,
          py: 0.35,
          bgcolor: isDark ? 'rgba(15,23,42,0.75)' : 'rgba(255,255,255,0.75)',
          backdropFilter: 'blur(4px)',
        }}
      >
        <Typography
          sx={{
            fontSize: '9px',
            fontWeight: 700,
            letterSpacing: '0.06em',
            color: isDark ? '#94a3b8' : '#6b7280',
          }}
        >
          OVERVIEW
        </Typography>
      </Box>
    </Box>
  )
}

export default React.memo(MapMinimap)
